/**
 * LessonView - Container component that displays the current lesson.
 * Routes to appropriate content renderer based on content type.
 */

import { memo, useState, useCallback, useMemo, useEffect } from 'react'
import { ChevronLeft, ChevronRight, CheckCircle2, Circle, Target } from 'lucide-react'
import { cn } from '../../lib/utils'
import { LessonContentRenderer } from './LessonContent'
import { QuizComponent } from './QuizComponent'
import { InteractiveExercise } from './InteractiveExercise'
import { SandboxExercise } from './SandboxExercise'
import type {
  Lesson,
  LessonCompletionState,
} from '../../curriculum/manager'
import { checkCompletion } from '../../curriculum/manager'
import type { OrderBook } from '../../engine/types'

export interface LessonViewProps {
  /** The lesson to display */
  lesson: Lesson
  /** Callback when lesson is completed */
  onComplete: (lessonId: string) => void
  /** Callback to navigate to a specific content index */
  onContentChange?: (index: number) => void
  /** Starting content index */
  initialContentIndex?: number
  /** Current order book for visualizations */
  orderBook?: OrderBook
  /** Current price for probability display */
  currentPrice?: number | null
  /** Market question for context */
  marketQuestion?: string
  /** Visualization mode */
  visualizationMode?: 'beginner' | 'advanced'
  /** Optional className */
  className?: string
}

/**
 * LessonView displays a lesson with navigation between content blocks.
 */
export const LessonView = memo(function LessonView({
  lesson,
  onComplete,
  onContentChange,
  initialContentIndex = 0,
  orderBook,
  currentPrice,
  marketQuestion,
  visualizationMode = 'beginner',
  className,
}: LessonViewProps) {
  const [currentIndex, setCurrentIndex] = useState(initialContentIndex)
  const [completionState, setCompletionState] = useState<LessonCompletionState>({
    contentProgress: 0,
    totalContent: lesson.content.length,
    quizScores: {},
    interactiveCompleted: {},
  })

  // Reset state when lesson changes
  useEffect(() => {
    setCurrentIndex(initialContentIndex)
    setCompletionState({
      contentProgress: 0,
      totalContent: lesson.content.length,
      quizScores: {},
      interactiveCompleted: {},
    })
  }, [initialContentIndex, lesson.id, lesson.content.length])

  const currentContent = lesson.content[currentIndex]
  const isFirstContent = currentIndex === 0
  const isLastContent = currentIndex === lesson.content.length - 1

  // Check if lesson is complete
  const isLessonComplete = useMemo(
    () => checkCompletion(lesson, completionState),
    [lesson, completionState]
  )

  // For quiz-pass lessons, calculate how many quizzes are passed
  const quizStatus = useMemo(() => {
    if (lesson.completionCriteria.type !== 'quiz-pass') {
      return null
    }
    const requiredScore = lesson.completionCriteria.minQuizScore ?? 70
    const quizIndices = lesson.content
      .map((c, i) => (c.type === 'quiz' ? i : -1))
      .filter((i) => i >= 0)

    const passed = quizIndices.filter(
      (idx) => (completionState.quizScores[idx] ?? 0) >= requiredScore
    ).length

    return { passed, total: quizIndices.length }
  }, [lesson, completionState.quizScores])

  // For interactive-complete lessons, calculate how many exercises are completed
  const interactiveStatus = useMemo(() => {
    if (lesson.completionCriteria.type !== 'interactive-complete') {
      return null
    }
    const interactiveIndices = lesson.content
      .map((c, i) => (c.type === 'interactive' ? i : -1))
      .filter((i) => i >= 0)

    const completed = interactiveIndices.filter(
      (idx) => completionState.interactiveCompleted[idx] === true
    ).length

    return { completed, total: interactiveIndices.length, indices: interactiveIndices }
  }, [lesson, completionState.interactiveCompleted])

  // Navigate to previous content
  const handlePrevious = useCallback(() => {
    if (!isFirstContent) {
      const newIndex = currentIndex - 1
      setCurrentIndex(newIndex)
      onContentChange?.(newIndex)
    }
  }, [currentIndex, isFirstContent, onContentChange])

  // Navigate to next content
  const handleNext = useCallback(() => {
    if (!isLastContent) {
      const newIndex = currentIndex + 1
      setCurrentIndex(newIndex)
      onContentChange?.(newIndex)

      // Update content progress
      setCompletionState((prev) => ({
        ...prev,
        contentProgress: Math.max(prev.contentProgress, newIndex + 1),
      }))
    }
  }, [currentIndex, isLastContent, onContentChange])

  // Handle quiz completion
  const handleQuizComplete = useCallback(
    (score: number) => {
      setCompletionState((prev) => ({
        ...prev,
        quizScores: { ...prev.quizScores, [currentIndex]: score },
        contentProgress: Math.max(prev.contentProgress, currentIndex + 1),
      }))
    },
    [currentIndex]
  )

  // Handle interactive completion
  const handleInteractiveComplete = useCallback(() => {
    setCompletionState((prev) => ({
      ...prev,
      interactiveCompleted: { ...prev.interactiveCompleted, [currentIndex]: true },
      contentProgress: Math.max(prev.contentProgress, currentIndex + 1),
    }))
  }, [currentIndex])

  // Handle lesson completion
  const handleFinish = useCallback(() => {
    // Mark final content as viewed
    setCompletionState((prev) => ({
      ...prev,
      contentProgress: prev.totalContent,
    }))
    onComplete(lesson.id)
  }, [lesson.id, onComplete])

  // Render content based on type
  const renderContent = () => {
    if (!currentContent) {
      return (
        <div className="py-12 text-center text-muted-foreground">
          No content available
        </div>
      )
    }

    switch (currentContent.type) {
      case 'text':
        return (
          <LessonContentRenderer
            content={currentContent.data}
            orderBook={orderBook}
            currentPrice={currentPrice}
            marketQuestion={marketQuestion}
            visualizationMode={visualizationMode}
          />
        )

      case 'quiz':
        return (
          <QuizComponent
            key={`quiz-${currentIndex}`}
            content={currentContent.data}
            onComplete={handleQuizComplete}
            previousScore={completionState.quizScores[currentIndex]}
          />
        )

      case 'interactive':
        return (
          <InteractiveExercise
            key={`interactive-${currentIndex}`}
            content={currentContent.data}
            onComplete={handleInteractiveComplete}
            isCompleted={completionState.interactiveCompleted[currentIndex]}
          />
        )

      case 'sandbox':
        return (
          <SandboxExercise
            key={`sandbox-${currentIndex}`}
            content={currentContent.data}
          />
        )

      default:
        return null
    }
  }

  return (
    <div
      className={cn('flex flex-col rounded-lg border border-border bg-card', className)}
      role="article"
      aria-label={`Lesson: ${lesson.title}`}
    >
      {/* Header */}
      <div className="border-b border-border p-4">
        <h2 className="text-lg font-semibold text-foreground">{lesson.title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{lesson.description}</p>
      </div>

      {/* Objectives */}
      {lesson.objectives.length > 0 && (
        <div className="border-b border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Target className="h-4 w-4" />
            <span>Learning Objectives</span>
          </div>
          <ul className="mt-2 space-y-1">
            {lesson.objectives.map((objective, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-sm text-muted-foreground"
              >
                <span className="mt-1 text-xs">•</span>
                <span>{objective}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 border-b border-border py-3">
        {lesson.content.map((content, idx) => {
          const isCompleted = idx < completionState.contentProgress
          const isCurrent = idx === currentIndex
          const isQuiz = content.type === 'quiz'
          const quizScore = completionState.quizScores[idx]
          const requiredScore = lesson.completionCriteria.type === 'quiz-pass'
            ? (lesson.completionCriteria.minQuizScore ?? 70)
            : 70
          const quizPassed = isQuiz && quizScore !== undefined && quizScore >= requiredScore
          const quizFailed = isQuiz && quizScore !== undefined && quizScore < requiredScore

          return (
            <button
              key={idx}
              type="button"
              onClick={() => {
                setCurrentIndex(idx)
                onContentChange?.(idx)
              }}
              className={cn(
                'transition-colors',
                isCurrent && 'text-primary',
                !isCurrent && quizPassed && 'text-green-500',
                !isCurrent && quizFailed && 'text-red-500',
                !isCurrent && isCompleted && !isQuiz && 'text-green-500',
                !isCurrent && !isCompleted && !quizPassed && !quizFailed && 'text-muted-foreground/50'
              )}
              aria-label={`Go to content ${idx + 1}${isQuiz ? ' (quiz)' : ''}${quizPassed ? ' - passed' : quizFailed ? ' - needs retry' : isCompleted ? ' (completed)' : ''}`}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {quizPassed && !isCurrent ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : quizFailed && !isCurrent ? (
                <Circle className="h-4 w-4 stroke-red-500" />
              ) : isCompleted && !isCurrent && !isQuiz ? (
                <CheckCircle2 className="h-4 w-4" />
              ) : (
                <Circle
                  className={cn('h-4 w-4', isCurrent && 'fill-current')}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Content area */}
      <div className="flex-1 p-4">{renderContent()}</div>

      {/* Navigation footer */}
      <div className="flex items-center justify-between border-t border-border p-4">
        <button
          type="button"
          onClick={handlePrevious}
          disabled={isFirstContent}
          className={cn(
            'flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            isFirstContent
              ? 'cursor-not-allowed text-muted-foreground/50'
              : 'text-foreground hover:bg-muted'
          )}
          aria-label="Previous content"
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </button>

        <span className="text-sm text-muted-foreground">
          {currentIndex + 1} of {lesson.content.length}
        </span>

        {isLastContent ? (
          <div className="flex flex-col items-end gap-1">
            {/* Show quiz progress hint when quizzes are required but not all passed */}
            {quizStatus && quizStatus.passed < quizStatus.total && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                Pass all quizzes to complete ({quizStatus.passed}/{quizStatus.total} passed)
              </span>
            )}
            {/* Show interactive progress hint when exercises are required but not all completed */}
            {interactiveStatus && interactiveStatus.completed < interactiveStatus.total && (
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs text-amber-600 dark:text-amber-400">
                  Complete all exercises ({interactiveStatus.completed}/{interactiveStatus.total} done)
                </span>
                <button
                  type="button"
                  onClick={() => {
                    // Navigate to first uncompleted interactive
                    const firstIncomplete = interactiveStatus.indices.find(
                      (idx) => !completionState.interactiveCompleted[idx]
                    )
                    if (firstIncomplete !== undefined) {
                      setCurrentIndex(firstIncomplete)
                      onContentChange?.(firstIncomplete)
                    }
                  }}
                  className="text-xs text-primary underline hover:no-underline"
                >
                  Go to exercise
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={handleFinish}
              disabled={!isLessonComplete && lesson.completionCriteria.type !== 'all-content'}
              className={cn(
                'flex items-center gap-1 rounded-md px-4 py-2 text-sm font-medium transition-colors',
                isLessonComplete || lesson.completionCriteria.type === 'all-content'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'cursor-not-allowed bg-muted text-muted-foreground'
              )}
              aria-label="Complete lesson"
            >
              <CheckCircle2 className="h-4 w-4" />
              Complete
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleNext}
            className="flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            aria-label="Next content"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
})
